import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import type { SettingsResponse } from '@/core/types';
import { LANG_NAMES } from '@/lib/lang-flags';
import { api } from '@/lib/api';

const SUPPORTED = Object.keys(LANG_NAMES);

/**
 * The languages this app has decks for.
 *
 * Distinct from the auxiliary list above, which is what things are explained
 * IN. This is what you are learning, and it is short because it is a fact about
 * the content that exists rather than a menu of possibilities.
 */
const TARGETS: { code: string; label: string }[] = [
  { code: 'de', label: 'German' },
  { code: 'pt-BR', label: 'Portuguese (Brazil)' },
];

function parseTargets(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed) && parsed.every((l) => typeof l === 'string'))
      return parsed as string[];
  } catch {
    // fall through
  }
  return ['de'];
}

export function LanguagesSection({ settings }: { settings: SettingsResponse }) {
  const [primary, setPrimary] = useState(settings.user.primary_aux_lang);
  const [secondary, setSecondary] = useState(settings.user.secondary_aux_lang);
  const [targets, setTargets] = useState<string[]>(() => parseTargets(settings.user.target_langs));
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: api.saveLanguages,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Language preferences saved');
    },
    onError: (err: Error) => toast.error('Failed to save', { description: err.message }),
  });

  const savedTargets = parseTargets(settings.user.target_langs);
  const dirty =
    primary !== settings.user.primary_aux_lang ||
    secondary !== settings.user.secondary_aux_lang ||
    targets.join() !== savedTargets.join();

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate({
          primary_aux_lang: primary,
          secondary_aux_lang: secondary,
          target_langs: targets,
        });
      }}
      className="flex flex-col gap-6"
    >
      <Field
        label="Languages you are learning"
        hint="Which decks appear on your home screen. More than one is allowed — running two courses at once is the only way to check a new one works."
      >
        <div className="flex flex-col gap-2">
          {TARGETS.map((target) => {
            const checked = targets.includes(target.code);
            return (
              <label key={target.code} className="flex items-center gap-2.5 text-sm">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    setTargets((current) => {
                      const next = checked
                        ? current.filter((c) => c !== target.code)
                        : [...current, target.code];
                      // Never leave the list empty: no target languages means an
                      // empty home screen, which reads as an outage rather than
                      // a choice.
                      return next.length > 0 ? next : current;
                    })
                  }
                  className="accent-primary size-4"
                />
                {target.label}
              </label>
            );
          })}
        </div>
      </Field>

      <Field
        label="Primary auxiliary language"
        hint="Always shown on the card back. Default English."
      >
        <NativeSelect value={primary} onChange={(e) => setPrimary(e.target.value)}>
          {SUPPORTED.map((code) => (
            <option key={code} value={code}>
              {LANG_NAMES[code]} ({code})
            </option>
          ))}
        </NativeSelect>
      </Field>

      <Field
        label="Secondary auxiliary language"
        hint="Your native language. Accepted as a valid translation answer."
      >
        <NativeSelect value={secondary} onChange={(e) => setSecondary(e.target.value)}>
          {SUPPORTED.map((code) => (
            <option key={code} value={code}>
              {LANG_NAMES[code]} ({code})
            </option>
          ))}
        </NativeSelect>
      </Field>

      <div>
        <Button type="submit" disabled={!dirty || mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}
